import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Input — text input with the design-system glass styling.
 *
 * Supports leftIcon (e.g. Search) and rightIcon (e.g. a kbd hint). For
 * errored inputs, set `hasError`; visual error state propagates to the
 * wrapping label if used.
 *
 * Optional a11y props: pass `label` to render an associated <label>, `hint`
 * for helper text, and `error` for an error message. When present, `error`
 * also drives aria-invalid and the error/hint is wired via aria-describedby.
 * Existing call sites pass none of these, so their behaviour is unchanged.
 */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hasError?: boolean;
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ leftIcon, rightIcon, hasError, label, error, hint, className, id, ...rest }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
    const invalid = hasError || !!error;

    const field = (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 bg-surface-container-lowest border border-outline-variant rounded-lg',
          'focus-within:border-primary/50',
          invalid && 'border-error/60 bg-error-container/30',
          className,
        )}
      >
        {leftIcon && <span className="text-text-muted shrink-0">{leftIcon}</span>}
        <input
          ref={ref}
          id={id ?? (label || describedById ? inputId : undefined)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedById}
          {...rest}
          className="flex-1 min-w-0 bg-transparent outline-none text-label placeholder:text-text-muted text-text-primary"
        />
        {rightIcon && <span className="text-text-muted shrink-0">{rightIcon}</span>}
      </div>
    );

    // No a11y wrappers requested → render exactly as before (no extra DOM,
    // no layout change for the ~30 existing call sites).
    if (!label && !error && !hint) return field;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-caption font-medium text-text-secondary">
            {label}
          </label>
        )}
        {field}
        {error ? (
          <span id={`${inputId}-error`} className="text-caption text-error">
            {error}
          </span>
        ) : hint ? (
          <span id={`${inputId}-hint`} className="text-caption text-text-muted">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
